import { useFetcher } from "@website/fetcher/use-fetcher";
import { MaterialIcon, MaterialNameType } from "@website/icons/material";
import { getTracking } from "@website/store/tracking";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { InfoChips } from "@website/ui/info-chips";
import { numberConvert } from "@website/utils/number-convert";
import {
  CheckboxItemEventTypes,
  FiltersSchemaType
} from "@website/widget/filter";
import mapValues from "lodash-es/mapValues";
import toString from "lodash-es/toString";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { handleTrackingEvent } from "../../map/index.tracking";
import { fetchPropertyDynamicFilters } from "./index.server";
import { handleCategoryBadgeFilterClicked } from "./index.tracking";

export const ratesInfoChips = {
  "4.5": {
    text: "عالی",
    color: "success"
  },
  "4": {
    text: "خیلی خوب",
    color: "success"
  },
  "3.5": {
    text: "خوب",
    color: "default"
  }
};

export const useVillaFiltersSchema = (
  isListingDataFetched: boolean
): {
  hasNearby: boolean;
  filtersSchema: FiltersSchemaType;
  chipsFiltersSchema: FiltersSchemaType;
  trackingCallback({ removed }: { removed: object }): void;
} => {
  const router = useRouter();

  const { data } = useFetcher({
    enabled: isListingDataFetched,
    queryKey: [
      "villa_dynamic_filters",
      String(router.query["locationType"]),
      String(router.query["cityTitle"]),
      Boolean(router.query["nearby"])
    ],
    queryFn: () =>
      fetchPropertyDynamicFilters(
        String(router.query["locationType"]),
        String(router.query["cityTitle"]),
        String(router?.query?.["date_from"]),
        String(router?.query?.["date_to"]),
        false,
        Boolean(router?.query?.["nearby"])
      ).then((data) => data)
  });

  useEffect(() => {}, [router.query.categories]);

  const onChangeCategoryBadge = (item: CheckboxItemEventTypes) => {
    handleCategoryBadgeFilterClicked({
      item_id: item.name,
      item: item.label,
      isChecked: item.isChecked,
      city_id: Number(router?.query?.["cityTitle"]),
      date_from: toString(router.query?.["date_from"]),
      date_to: toString(router.query?.["date_to"])
    });
  };

  const chipsFiltersSchema: FiltersSchemaType = useMemo(
    () => ({
      ...(data?.facilities?.length > 0
        ? {
            facilities: {
              type: "checkbox",
              title: "امکانات اقامتگاه",
              collapsible: data?.facilities.length >= 5,
              options: data?.facilities?.map((facility) => ({
                name: String(facility?.id),
                label: facility?.title,
                icon: (
                  <MaterialIcon
                    className="text-on-surface-medium-emphasis text-headline-5"
                    name={
                      facility.icon === "what.ico" || !facility.icon
                        ? "check_box"
                        : (facility.icon as MaterialNameType)
                    }
                    variant="outlined"
                  />
                )
              }))
            }
          }
        : {}),
      price_range: {
        title: "قیمت",
        description: "مجموع قیمت",
        type: "range",
        min_key: "price_from",
        max_key: "price_to",
        step: 100_000,
        min: data?.priceRange?.min,
        max: data?.priceRange?.max
      },
      ...(data?.accommodations?.length > 0
        ? {
            type: {
              type: "checkbox",
              title: "نوع اقامتگاه",
              isNew: true,
              collapsible: data?.accommodations?.length > 5,
              options:
                data?.accommodations?.map((accommodation) => ({
                  name: accommodation.type,
                  label: accommodation.label
                })) || []
            }
          }
        : {})
    }),
    [data]
  );

  return {
    filtersSchema: {
      q: {
        type: "input",
        title: "جستجو بر اساس نام اقامتگاه"
      },
      price_range: {
        title: "قیمت",
        description: "مجموع قیمت",
        type: "range" as const,
        min_key: "price_from",
        max_key: "price_to",
        step: 100_000,
        min: data?.priceRange?.min,
        max: data?.priceRange?.max
      },
      ...(data?.accommodations?.length > 0
        ? {
            type: {
              type: "checkbox" as const,
              title: "نوع اقامتگاه",
              hasSearch: false,
              collapsible: data?.accommodations?.length > 5,
              options:
                data?.accommodations?.map((accommodation) => ({
                  name: accommodation.type,
                  label: accommodation.label
                })) || []
            }
          }
        : {}),
      ...(data?.rates.length > 0
        ? {
            rate: {
              type: "radio",
              title: "امتیاز کاربران",
              defaultValue: "all",
              items: [
                { value: "all", label: "همه" },
                ...(data?.rates || []).map((rate) => ({
                  value: rate === 0 ? "new" : String(rate),
                  icon: ratesInfoChips[String(rate)] ? (
                    <InfoChips
                      StartIcon={
                        <MaterialIcon className="text-body-2" name="stars" />
                      }
                      color={ratesInfoChips[String(rate)]?.color || ""}
                      size="medium"
                      text={ratesInfoChips[String(rate)]?.text || ""}
                      variant="round-accent"
                    />
                  ) : undefined,
                  label:
                    rate === 0
                      ? "اقامتگاه‌های بدون امتیاز"
                      : `${numberConvert(rate, {
                          locale: router.locale
                        })}+ امتیاز`
                }))
              ]
            }
          }
        : {}),
      ...(data?.nearby &&
      getTracking()?.experiment?.experiment_nearby_cities_toggle === "true"
        ? {
            nearby: {
              type: "switch",
              title: "نمایش ویلاهای اطراف"
            }
          }
        : {}),
      ...(data?.geo_property_summary?.length > 1 &&
      (router.query?.locationType === "state" ||
        router.query?.locationType === "city" ||
        router.query?.locationType === "category")
        ? {
            geo_property_summary: {
              type: "checkbox",
              title: "انتخاب شهر",
              isNew: true,
              collapsible: data?.geo_property_summary.length > 5,
              options: data?.geo_property_summary?.map((geo_property) => ({
                name: `${String(geo_property?.geo_slug)}___${String(
                  geo_property?.geo_type
                )}___${router.query?.["nearby"]}`,
                label: geo_property?.title
                // icon: (
                //   <InfoChips
                //     color={"dim"}
                //     size="medium"
                //     text={`${numberConvert(geo_property?.property_count, {
                //       locale: router.locale
                //     })} اقامتگاه`}
                //     variant="text"
                //   />
                // )
              }))
            }
          }
        : {}),
      categories: {
        type: "checkbox" as const,
        title: "پیشنهادات ویژه",
        onChange: onChangeCategoryBadge,
        options: [
          {
            label: "رزرو آنی",
            name: "instant"
          },
          ...(data?.categories?.map((category) => ({
            name: String(category.id),
            label: category.title
          })) || [])
        ]
      },
      ...(data?.facilities?.length > 0
        ? {
            facilities: {
              type: "checkbox",
              title: "امکانات اقامتگاه",
              collapsible: data?.facilities.length > 5,
              options: data?.facilities?.map((facility) => ({
                name: String(facility?.id),
                label: facility?.title,
                icon: (
                  <MaterialIcon
                    className="text-on-surface-medium-emphasis text-headline-5"
                    name={
                      facility.icon === "what.ico" || !facility.icon
                        ? "check_box"
                        : (facility.icon as MaterialNameType)
                    }
                    variant="outlined"
                  />
                )
              }))
            }
          }
        : {})
    },
    hasNearby: data?.nearby,
    chipsFiltersSchema,
    trackingCallback: ({ removed }: { removed: object }) => {
      mapValues(removed, (value, key) =>
        handleTrackingEvent(USER_TRACKING_EVENTS.FILTER_PILL_REMOVED, {
          removed_filter_name: key,
          removed_filter_value: value
        })
      );
    }
  };
};
